import { Snackbar, useTheme } from '@mui/material';
import { setTag, setUser as setSentryUser } from '@sentry/react';
import { signInWithCustomToken } from 'firebase/auth';
import { AuthUser } from 'interfaces/user';
import { useState, ReactNode, useEffect, useCallback } from 'react';
import { logout } from 'services/auth';
import { auth } from 'services/firebase';
import { getPrefixedRoute, getThemeVariant } from 'services/theming';
import useUser, { getUserFromParsedIdToken } from 'hooks/useUser';
import { getAuthErrorDetails, recordAuthEvent } from 'utils/auth-instrumentation';
import { isInvalidSessionIdError, isNetworkError } from 'utils/firebase-error-checks';
import { setNativeSentryUser } from 'utils/nile-app';
import refreshFirebaseUser from 'utils/refresh-firebase-user';
import { setUserAnalytics } from 'utils/setUserAnalytics';

const APP_TOKEN_STORAGE_KEY = 'appToken';

const AuthCheck = ({ children }: { children: ReactNode }) => {
  const theme = useTheme();
  const [isInitialised, setIsInitialised] = useState(false);
  const [message, setMessage] = useState('');
  const { user, setUser } = useUser();

  const handleUserRefresh = useCallback(async () => {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        await logout(false, undefined, {
          source: 'AuthCheck.handleUserRefresh',
          reason: 'no firebase user on init refresh',
        });
        return;
      }
      await refreshFirebaseUser({
        user: firebaseUser,
        setUser,
      });
    } catch (error) {
      recordAuthEvent('session-reset', {
        source: 'AuthCheck.handleUserRefresh',
        reason: 'refresh failed during init',
        ...getAuthErrorDetails(error),
      });
      setMessage('Failed to authenticate');
      window.location.replace(getPrefixedRoute('/login'));
    } finally {
      setIsInitialised(true);
    }
  }, [setUser]);

  const handleAppTokenLogin = useCallback(
    async (appToken: string) => {
      // Validate token is not empty or just whitespace
      if (!appToken || !appToken.trim()) {
        localStorage.removeItem(APP_TOKEN_STORAGE_KEY);
        handleUserRefresh();
        return;
      }

      // Skip custom token login when offline - it requires network connection
      if (!navigator.onLine) {
        // Keep the token for when we come back online
        handleUserRefresh();
        return;
      }

      try {
        const userCredential = await signInWithCustomToken(auth, appToken.trim());
        const authUser = await userCredential?.user?.getIdTokenResult(true);

        if (!authUser) {
          throw new Error('Failed to get user please try log in again');
        }

        if (authUser?.claims?.status === 'Rejected') {
          throw new Error('Your profile is not active, contact support for information');
        }

        localStorage.removeItem(APP_TOKEN_STORAGE_KEY);
        setUser(getUserFromParsedIdToken(authUser));
        setIsInitialised(true);
      } catch (error: any) {
        // Handle specific Firebase auth errors using utility functions
        if (isInvalidSessionIdError(error)) {
          // Invalid session ID - clear the token immediately
          localStorage.removeItem(APP_TOKEN_STORAGE_KEY);
          // Fall back to regular auth flow
          handleUserRefresh();
        } else if (isNetworkError(error)) {
          // Network error - preserve token for retry when online
          // Don't clear token, just use existing auth state
          handleUserRefresh();
        } else {
          // Other errors - clear token and fall back
          localStorage.removeItem(APP_TOKEN_STORAGE_KEY);
          handleUserRefresh();
        }
      }
    },
    [setUser, handleUserRefresh],
  );

  // Safety: ensure we never leave the user on a permanent white screen if auth never settles (e.g. slow/failing network on mobile)
  const INIT_TIMEOUT_MS = 15_000;
  useEffect(() => {
    if (isInitialised) return;
    const t = setTimeout(() => setIsInitialised(true), INIT_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [isInitialised]);

  useEffect(() => {
    if (isInitialised) return;

    try {
      const appToken = localStorage.getItem(APP_TOKEN_STORAGE_KEY);
      // Only attempt login if token exists and is not empty
      if (appToken && appToken.trim()) {
        handleAppTokenLogin(appToken);
      } else {
        // Clear invalid/empty token
        if (appToken !== null) {
          localStorage.removeItem(APP_TOKEN_STORAGE_KEY);
        }
        handleUserRefresh();
      }
    } catch (error) {
      handleUserRefresh();
    }
  }, [handleAppTokenLogin, handleUserRefresh, isInitialised]);

  useEffect(() => {
    if (!isInitialised) {
      return;
    }
    if (!user) {
      setSentryUser(null);
      setNativeSentryUser(null);
      return;
    }
    const sentryUser = { id: user.userId, email: user.email };
    setSentryUser(sentryUser);
    setNativeSentryUser(sentryUser);
    setTag('theme', getThemeVariant());

    Object.keys(user).forEach((key) => {
      try {
        setTag(key, JSON.stringify(user[key as keyof AuthUser]));
      } catch {
        return;
      }
    });

    setUserAnalytics(user.email);
  }, [user, theme, isInitialised]);

  if (!isInitialised) {
    return null;
  }

  return (
    <>
      {children}
      <Snackbar open={!!message} onClose={() => setMessage('')} message={message} />
    </>
  );
};

export default AuthCheck;
